import React, { useState } from "react";

import { Box, Input } from "@chakra-ui/react";
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg";
import { ReactComponent as UploadedFileIcon } from "assets/icons/file-upload-icon.svg";
import { ReactComponent as UploadIcon } from "assets/icons/upload-icon.svg";
import { MAX_FILE_UPLOAD_SIZE, getFileSize } from "utils";
import { START_INDEX } from "utils/constants";
import { Maybe } from "utils/general/maybe";

import { customErrorToast } from "ui/custom-toast";
import { spinnerIconBlack } from "ui/filter/filter-footer";
import { Paragraph2 } from "ui/typography";

import { IFileUploadProps } from "./types";

export const FileUpload = (props: IFileUploadProps) => {
  const {
    name,
    fileUploadLabel,
    file,
    handleFileChange,
    doesUploadedFileContainerHaveFullWidth,
    fileTypes,
    uploadFileEmptyStateContainerCss,
    fileUploadLabelCss,
  } = props;
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    setIsDragging(false);

    if (evt.dataTransfer.files && evt.dataTransfer.files.length > START_INDEX) {
      const [uploadedFile] = evt.dataTransfer.files;
      handleFileChange(name, uploadedFile);
      evt.dataTransfer.clearData();
    }
  };

  const handleFileSelect = (evt: React.ChangeEvent<HTMLInputElement>) => {
    if (evt.target.files) {
      const [selectedFile] = evt.target.files;
      if (selectedFile.size > MAX_FILE_UPLOAD_SIZE) {
        customErrorToast({
          message: "File size must be less than 10MB.",
        });
        evt.target.value = "";
        return;
      }
      handleFileChange(name, selectedFile);
    }
  };

  const handleDeSelectFile = () => {
    handleFileChange(name, null);
  };

  return (
    <Box className="flex flex-col gap-2 h-fit">
      <Maybe condition={Boolean(fileUploadLabel)}>
        <Paragraph2
          fontWeight="600"
          lineHeight="20px"
          textColor="#1A202C"
          style={fileUploadLabelCss}
        >
          {fileUploadLabel}
        </Paragraph2>
      </Maybe>
      {file && file instanceof File && (
        <Box
          className={`h-[68px] rounded-lg py-[14px] px-4 bg-[#FAFAFA] border border-[#EEEEEE] flex items-center gap-1 w-full ${doesUploadedFileContainerHaveFullWidth ? "max-w-full" : "max-w-[76%]"}`}
        >
          <Box className="flex items-center gap-[10px] w-full max-w-[calc(100%-20px)]">
            <UploadedFileIcon className="flex-shrink-0 w-[30px] h-[40px]" />
            <Box className="flex flex-col justify-between w-full max-w-[calc(100%-60px)]">
              <p className="text-sm text-[#00535C] font-semibold  text-left whitespace-nowrap text-ellipsis overflow-hidden w-fit max-w-full min-w-[59%]">
                {file?.name}
              </p>
              <p className="text-sm text-[#667085] font-semibold w-fit max-w-full text-right">
                {file?.size && getFileSize(file.size)}
              </p>
            </Box>
          </Box>
          <DeleteIcon
            className="w-5 h-5 flex-shrink-0 cursor-pointer"
            onClick={handleDeSelectFile}
          />
        </Box>
      )}
      {file && typeof file === "string" && (
        <Box
          className={`h-[68px] rounded-lg py-[14px] px-4 bg-[#FAFAFA] border border-[#EEEEEE] flex items-center gap-1 w-full ${doesUploadedFileContainerHaveFullWidth ? "max-w-full" : "max-w-[76%]"}`}
        >
          <Box className="flex items-center gap-[10px] w-full max-w-[calc(100%-20px)]">
            <UploadedFileIcon className="flex-shrink-0 w-[30px] h-[40px]" />
            <Box className="flex flex-col justify-between w-full max-w-[calc(100%-60px)]">
              <a
                href={file}
                download
                className="text-sm text-[#00535C] font-semibold  text-left whitespace-nowrap text-ellipsis overflow-hidden w-full h-fit max-w-[300px]"
              >
                {file}
              </a>
            </Box>
          </Box>
          <DeleteIcon
            className="w-5 h-5 flex-shrink-0 cursor-pointer"
            onClick={handleDeSelectFile}
          />
        </Box>
      )}

      <Maybe condition={!file}>
        <Box
          className="h-[104px] rounded-lg border border-[#E4E7EC] border-dashed py-4 px-6 bg-white"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          css={uploadFileEmptyStateContainerCss}
        >
          <Maybe condition={isDragging}>
            <Box className="flex flex-col gap-3 justify-center cursor-not-allowed">
              <Box className="flex items-center justify-center">
                {spinnerIconBlack}
              </Box>
              <Box className="flex items-center justify-center gap-1">
                <p className="text-sm text-[#00535C] font-semibold">
                  Dropping file...
                </p>
              </Box>
            </Box>
          </Maybe>
          <Maybe condition={!isDragging}>
            <label
              htmlFor={name}
              className="flex flex-col gap-3 justify-center cursor-pointer"
            >
              <Box className="flex items-center justify-center">
                <UploadIcon className="w-10 h-10 flex-shrink-0" />
              </Box>
              <Box className="flex items-center justify-center gap-1">
                <p className="text-sm text-[#00535C] font-semibold">
                  Click to upload
                </p>
                <p className="text-sm text-[#667085]"> or drag and drop</p>
              </Box>
            </label>
          </Maybe>
          <Input
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            id={name}
            accept={fileTypes.map((fileType) => `.${fileType}`).join(",")}
          />
        </Box>
      </Maybe>
    </Box>
  );
};
